import type { SflagaEnhancementOptions } from '../types';
import { createSeededRNG } from './seededRNG';

/**
 * Shared benchmark parameter policy used to keep cross-algorithm comparisons fair.
 *
 * The goal is not per-algorithm tuning. Instead, the app derives one neutral
 * parameter set from workflow size and applies that same experimental budget
 * across the algorithms in a run.
 */

export const SHARED_PARAMETER_POLICY = 'shared-benchmark-v1';
export const RANDOMIZED_SHARED_PARAMETER_POLICY = 'shared-benchmark-random-v2';
export const SHARED_PARAMETER_POLICY_LABEL = 'Shared benchmark configuration';
export const DEFAULT_SFLAGA_ENHANCEMENTS: SflagaEnhancementOptions = {
  guidedMutation: true,
  elitePreservation: true,
  similarityAwareDiversification: true,
  adaptiveImmigrantRefresh: true,
  worstFrogLocalSearch: true,
};

export interface SharedBenchmarkParameters {
  iterations: number;
  initialPopulation: number;
  selectionRate: number;
  crossoverRate: number;
  mutationRate: number;
  memeplexCount: number;
  parameterPolicy: string;
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

/**
 * Derives a common experimental configuration from workflow size.
 *
 * Shared by design:
 * - Population size and iterations are common to every algorithm.
 * - Selection, crossover, and mutation rates are common to the algorithms that
 *   use those operators.
 * - Memeplex count is common to the SFLA-family algorithms.
 */
export function getSharedBenchmarkParameters(taskCount: number): SharedBenchmarkParameters {
  const initialPopulation = clamp(taskCount + 40, 50, 700);
  const iterations = clamp(Math.floor(taskCount / 10) + 40, 40, 500);

  let crossoverRate = 80;
  let mutationRate = 12;

  if (taskCount <= 20) {
    crossoverRate = 75;
    mutationRate = 15;
  } else if (taskCount > 100) {
    crossoverRate = 85;
    mutationRate = 10;
  }

  return {
    iterations,
    initialPopulation,
    selectionRate: 60,
    crossoverRate,
    mutationRate,
    memeplexCount: clamp(Math.round(initialPopulation / 20), 4, 35),
    parameterPolicy: SHARED_PARAMETER_POLICY,
  };
}

export function getRandomizedBenchmarkParameters(
  taskCount: number,
  seed: number
): SharedBenchmarkParameters {
  const baseline = getSharedBenchmarkParameters(taskCount);
  const rng = createSeededRNG(seed);

  const initialPopulationMin = clamp(
    Math.round(baseline.initialPopulation * 0.75),
    50,
    700
  );
  const initialPopulationMax = clamp(
    Math.round(baseline.initialPopulation * 1.25),
    initialPopulationMin,
    700
  );
  const initialPopulation = rng.randomIntRange(
    initialPopulationMin,
    initialPopulationMax
  );

  const iterationsMin = clamp(Math.round(baseline.iterations * 0.5), 40, 500);
  const iterationsMax = clamp(
    Math.round(baseline.iterations * 1.75),
    iterationsMin,
    500
  );
  const iterations = rng.randomIntRange(iterationsMin, iterationsMax);

  const selectionRate = rng.randomIntRange(20, 90);
  const crossoverRate = rng.randomIntRange(45, 95);
  const mutationRate = rng.randomIntRange(5, 35);

  const memeplexCount = rng.randomIntRange(4, 35);

  return {
    iterations,
    initialPopulation,
    selectionRate,
    crossoverRate,
    mutationRate,
    memeplexCount,
    parameterPolicy: RANDOMIZED_SHARED_PARAMETER_POLICY,
  };
}

/**
 * Backward-compatible alias kept for existing imports and docs.
 */
export function getOptimalParametersForTaskCount(taskCount: number): SharedBenchmarkParameters {
  return getSharedBenchmarkParameters(taskCount);
}

export function resolveSflagaEnhancements(
  value?: Partial<SflagaEnhancementOptions> | null
): SflagaEnhancementOptions {
  return {
    ...DEFAULT_SFLAGA_ENHANCEMENTS,
    ...(value ?? {}),
  };
}
